import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { changeTableSearch } from '../../../../redux/id8/id8Reducer';
import { ID8_TABS } from '../../../../constants';
import { BUTTON_STYLES } from '../../../../components/common-button/CommonButton';
import Icon from '../../../../icons/Icon';
import { ICONS } from '../../../../icons';
import SearchInput from '../../../../components/common-inputs/SearchInput';
import CreateMessageModal from '../../../../scout-new/components/create-message-modal/CreateMessageModal';
import ConfirmationModal from '../../../../components/confirmation-modal/ConfirmationModal';
import TooltipButton from '../../../../components/tooltip-button/TooltipButton';
import useIsMessagesGenerating from '../../../hooks/useIsMessagesGenerating';

import scss from './MessageFilters.module.scss';

const MessageFilters = ({ onClearBoard, onClearAll, onExportMessages, onCreateMessage }) => {
  const { t } = useTranslation('id8');
  const dispatch = useDispatch();

  const { id } = useParams();

  const [isShowCreateMessageModal, setIsShowCreateMessageModal] = useState(false);
  const [isClearAllModalOpen, setIsClearAllModalOpen] = useState(false);

  const isGenerating = useIsMessagesGenerating();
  const id8Tab = useSelector((state) => state.id8.id8Tab);

  const searchQuery = useSelector((state) => state.id8.tableSettings.searchQuery);

  const mapButtonText = {
    [ID8_TABS.IDEAS]: t('generate'),
  };

  const handleCreateMessage = async ({ projectId, messages }) => {
    await onCreateMessage({ projectId, messages });
  };

  const handleChangeSearch = (newSearch) => {
    dispatch(changeTableSearch(newSearch));
  };

  const handleOpenCreateMessageModal = () => {
    setIsShowCreateMessageModal(true);
  };

  const handleCloseCreateMessageModal = () => {
    setIsShowCreateMessageModal(false);
  };

  const handleOpenClearAllModal = () => {
    setIsClearAllModalOpen(true);
  };

  const handleCloseClearAllModal = () => {
    setIsClearAllModalOpen(false);
  };

  const handleClearBoard = () => {
    dispatch(onClearBoard());
  };

  const handleClearAll = () => {
    dispatch(onClearAll(id));
  };

  const handleExportMessages = () => {
    dispatch(onExportMessages());
  };

  return (
    <div className={scss.filtersWrapper}>
      <div className={scss.leftSection}>
        <TooltipButton
          styleColor={BUTTON_STYLES.PRIMARY}
          type='submit'
          form='id8_generation_form'
          maxWidth={160}
          isLoading={isGenerating}
          className={scss.generateButton}
          tooltip={isGenerating && t('messagesGenerating')}>
          {mapButtonText[id8Tab]}
        </TooltipButton>

        <TooltipButton
          styleColor={BUTTON_STYLES.SECONDARY}
          onClick={handleOpenCreateMessageModal}
          disabled={isGenerating}
          tooltip={isGenerating && t('messagesGenerating')}
          className={scss.customMessageButton}>
          <Icon icon={ICONS.Edit} disableCommonColors />
          {t('customMessage')}
        </TooltipButton>
      </div>

      <div className={scss.rightSection}>
        <SearchInput
          value={searchQuery}
          disabled={isGenerating}
          onChange={handleChangeSearch}
          size='sm'
          placeholder={t('searchByWord')}
          maxWidth={280}
          extraWrapperClass={scss.inputMedium}
        />

        {onClearBoard ? (
          <TooltipButton
            styleColor={BUTTON_STYLES.SECONDARY}
            maxWidth={150}
            onClick={handleClearBoard}
            disabled={isGenerating}
            tooltip={isGenerating && t('messagesGenerating')}
            isStrokeIcon>
            {t('clearBoard')}
            <Icon icon={ICONS.Clean} disableCommonColors className={scss.cleanIcon} />
          </TooltipButton>
        ) : null}

        {onClearAll ? (
          <TooltipButton
            styleColor={BUTTON_STYLES.SECONDARY}
            maxWidth={130}
            onClick={handleOpenClearAllModal}
            disabled={isGenerating}
            tooltip={isGenerating && t('messagesGenerating')}
            isStrokeIcon>
            {t('clearAll')}
            <Icon icon={ICONS.Cross} disableCommonColors className={scss.crossIcon} />
          </TooltipButton>
        ) : null}

        <TooltipButton
          styleColor={BUTTON_STYLES.SECONDARY}
          maxWidth={160}
          disabled={isGenerating}
          tooltip={isGenerating && t('messagesGenerating')}
          onClick={handleExportMessages}>
          {t('exportMessages')}
          <Icon icon={ICONS.ArrowUp} disableCommonColors />
        </TooltipButton>
      </div>

      <CreateMessageModal
        isOpen={isShowCreateMessageModal}
        onClose={handleCloseCreateMessageModal}
        onCreateMessage={handleCreateMessage}
      />

      <ConfirmationModal
        isOpen={isClearAllModalOpen}
        onClose={handleCloseClearAllModal}
        onCancel={handleCloseClearAllModal}
        onConfirm={handleClearAll}
        label={t('areYouSureYouWantToDeleteMessageHistory')}
        subLabel={t('thisCanNotBeReversed')}
      />
    </div>
  );
};

export default memo(MessageFilters);
